import { ClientSideDataGridModel } from "src/components/common/dataGrid/gridModel/ClientSideDataGridModel.tsx";
import { FormCrudDataGrid } from "src/components/common/dataGrid/crud/FormCrudDataGrid.tsx";
import {
	dateTimeColumn,
	enumColumn,
	floatColumn,
	integerColumn,
	textColumn,
} from "src/components/common/dataGrid/columns.tsx";
import i18n from "i18next";
import { getShopOrderStateLabels } from "src/api/generated/erp/db/types/enums/shopOrderState.ts";
import { ShopOrderView } from "src/api/generated/erp/db/types/tables/shopOrderView.ts";
import { AsyncButton } from "src/components/common/buttons/AsyncButton.tsx";
import { faLink, faPaperclip, faTh, faTrash } from "@fortawesome/pro-regular-svg-icons";
import { OpenShopOrderButton } from "src/components/views/erp/utilComponents/OpenShopOrderButton.tsx";
import { OpenCustomerOrderButton } from "src/components/views/erp/utilComponents/OpenCustomerOrderButton.tsx";
import { OpenSitePartButton } from "src/components/views/erp/utilComponents/OpenSitePartButton.tsx";
import { useGenericDialog } from "src/components/common/dialogs/GenericDialogContext.ts";
import { RefreshableElementRef } from "src/utils/useRefreshRef.ts";
import { AsyncMenuButton } from "src/components/common/contextMenu/AsyncMenuButton.tsx";
import { ShopOrderStateView } from "src/components/views/erp/production/shopOrder/ShopOrderStateView.tsx";
import { ShopOrderBatchShopOrdersApi } from "src/api/generated/erp/production/shopOrderBatch/api/shopOrderBatchShopOrdersApi.ts";
import { ShopOrderBatchState } from "src/api/generated/erp/db/types/enums/shopOrderBatchState.ts";
import { AddShopOrdersToBatchView } from "src/components/views/erp/production/shopOrderBatch/AddShopOrdersToBatchView.tsx";
import { DocumentsOfSingleObjectView } from "src/components/views/documents/objectDocuments/DocumentsOfSingleObjectView.tsx";
import { AavoObjectTypeApi } from "src/api/generated/common/aavoObject/aavoObjectTypeApi.ts";
import { useTenantCustomizations } from "src/tenantCustomizations/TenantCustomizationsContext.ts";
import { CellWithRemarkIndicator } from "src/components/common/dataGrid/cells/CellWithRemarkIndicator.tsx";
import { defineAgentUiContextKey } from "src/components/views/ai/assistant/uiContext/agentUiContextKey.ts";
import { ShopOrderBatchUpdateAttributesMenuButton } from "src/components/views/erp/production/shopOrderBatch/ShopOrderBatchUpdateAttributesMenuButton.tsx";

export interface ShopOrderBatchShopOrdersDataGridProps {
	shopOrderBatchId: number;
	productionLineId: number | null | undefined;
	shopOrderBatchState: ShopOrderBatchState;
	refreshRef?: RefreshableElementRef;
	shopOrderBatchesRefreshRef?: RefreshableElementRef;
	editable?: boolean;
}

export const ShopOrderBatchShopOrdersDataGrid = ({
	shopOrderBatchId,
	productionLineId,
	shopOrderBatchState,
	refreshRef,
	shopOrderBatchesRefreshRef,
	editable = true,
}: ShopOrderBatchShopOrdersDataGridProps) => {
	const { openDialog } = useGenericDialog();
	const { tenantConfig } = useTenantCustomizations();
	return (
		<ClientSideDataGridModel
			gridId={"EC9991492B12B3B2AB11"}
			fetchData={async () =>
				await ShopOrderBatchShopOrdersApi.getShopOrderBatchShopOrders({
					shopOrderBatchId: shopOrderBatchId,
				})
			}
			getRows={(data) => data}
			getRowId={(row) => row.shopOrderId}
			customFunctionUiActionView={"SHOP_ORDERS"}
			initialParams={{}}
			refreshRef={refreshRef}
			render={({ dataGridProps, selectedRows, refreshData }) => {
				return (
					<FormCrudDataGrid<ShopOrderView>
						columns={[
							integerColumn({
								field: "shopOrderId",
								headerName: i18n.t("number_short"),
							}),
							textColumn({
								field: "sourceDescription",
								headerName: i18n.t("reference"),
							}),
							textColumn({
								field: "configurationOrPartDescription",
								headerName: i18n.t("part"),
								width: 200,
								renderCell: ({ row }) => (
									<CellWithRemarkIndicator
										value={row.configurationOrPartDescription}
										remark={[
											{ label: i18n.t("sales_remark"), value: row.partSalesRemark },
											{ label: i18n.t("acquisition_remark"), value: row.partAcquisitionRemark },
										]}
									/>
								),
							}),
							floatColumn({
								field: "quantity",
								headerName: i18n.t("quantity"),
							}),
							textColumn({
								field: "partUnit",
								headerName: i18n.t("unit"),
							}),
							enumColumn({
								field: "shopOrderState",
								headerName: i18n.t("state"),
								enumLabels: getShopOrderStateLabels(),
							}),
							dateTimeColumn({
								field: "demandDate",
								headerName: i18n.t("demand_date"),
								width: 150,
							}),
							floatColumn({
								field: "capacityQuantity",
								headerName: i18n.t("capacity_quantity"),
								width: 140,
							}),
							dateTimeColumn({
								field: "plannedBeginDate",
								headerName: i18n.t("planned_begin_date_short"),
								width: 130,
							}),
							dateTimeColumn({
								field: "plannedEndDate",
								headerName: i18n.t("planned_end_date_short"),
								width: 130,
							}),
							floatColumn({
								field: "totalEstimatedProductionTimeHours",
								headerName: i18n.t("estimated_production_time"),
								width: 200,
							}),
						]}
						actionBarComponents={
							<>
								{editable && shopOrderBatchState === "INITIAL" && (
									<AsyncButton
										label={i18n.t("remove_from_batch")}
										icon={faTrash}
										variant={"outlined"}
										disabled={selectedRows.length === 0}
										onClick={async () => {
											await ShopOrderBatchShopOrdersApi.removeShopOrdersFromBatch({
												shopOrderBatchId: shopOrderBatchId,
												shopOrderIds: selectedRows.map((r) => r.shopOrderId),
											});
											await Promise.all([refreshData(), shopOrderBatchesRefreshRef?.refresh()]);
										}}
									/>
								)}
								{editable && (
									<AsyncButton
										label={i18n.t("connect_shop_orders")}
										icon={faLink}
										disabled={
											shopOrderBatchState == "CANCELLED" ?
												i18n.t("shop_order_batch_is_in_cancelled_state")
											:	undefined
										}
										variant={"outlined"}
										onClick={() => {
											openDialog(({ closeDialog }) => ({
												title: i18n.t("connect_shop_orders"),
												agentContext: {
													key: defineAgentUiContextKey("erp.production.connectShopOrdersToBatch.dialog"),
													title: i18n.t("connect_shop_orders"),
													data: {
														shopOrderBatchId,
														productionLineId: productionLineId ?? null,
													},
												},
												content: (
													<AddShopOrdersToBatchView
														shopOrderBatchId={shopOrderBatchId}
														defaultProductionLineId={productionLineId}
														onCompleted={async () => {
															await closeDialog();
															await Promise.all([
																refreshData(),
																shopOrderBatchesRefreshRef?.refresh(),
															]);
														}}
													/>
												),
											}));
										}}
									/>
								)}
								{editable && (
									<AsyncButton
										label={i18n.t("connect_same_parts")}
										icon={faLink}
										disabled={
											shopOrderBatchState == "CANCELLED" ?
												i18n.t("shop_order_batch_is_in_cancelled_state")
											:	undefined
										}
										variant={"outlined"}
										onClick={() => {
											openDialog(({ closeDialog }) => ({
												title: i18n.t("connect_same_parts"),
												agentContext: {
													key: defineAgentUiContextKey("erp.production.connectSamePartsToBatch.dialog"),
													title: i18n.t("connect_same_parts"),
													data: {
														shopOrderBatchId,
														productionLineId: productionLineId ?? null,
													},
												},
												content: (
													<AddShopOrdersToBatchView
														shopOrderBatchId={shopOrderBatchId}
														defaultProductionLineId={productionLineId}
														onlyAlreadyConnectedParts={true}
														onCompleted={async () => {
															await closeDialog();
															await Promise.all([
																refreshData(),
																shopOrderBatchesRefreshRef?.refresh(),
															]);
														}}
													/>
												),
											}));
										}}
									/>
								)}
							</>
						}
						rowContextMenuComponents={({ row, allSelectedRows }) => [
							<AsyncMenuButton
								key={"snapshot"}
								label={i18n.t("snapshot")}
								icon={faTh}
								onClick={async () => {
									openDialog({
										title: i18n.t("snapshot"),
										size: "xl",
										agentContext: {
											key: defineAgentUiContextKey("erp.production.shopOrderBatchShopOrderSnapshot.dialog"),
											title: i18n.t("snapshot"),
											pathSegments: [`${i18n.t("shop_order")} ${row.shopOrderId}`],
											data: {
												shopOrderBatchId,
												shopOrderId: row.shopOrderId,
											},
										},
										content: <ShopOrderStateView shopOrderId={row.shopOrderId} />,
									});
								}}
							/>,
							tenantConfig.documentsEnabled && (
								<AsyncMenuButton
									key={"partRevisionDocuments"}
									label={i18n.t("part_revision_documents")}
									icon={faPaperclip}
									onClick={async () => {
										const objectRef = {
											objectType: "CATALOG_PART_REVISION" as const,
											objectId: row.catalogPartRevisionId,
										};
										const description = await AavoObjectTypeApi.getAavoObjectDescription({
											objectRef: objectRef,
										});
										openDialog(() => ({
											title: `${i18n.t("part_revision_documents")} (${description})`,
											agentContext: {
												key: defineAgentUiContextKey("erp.production.shopOrderPartRevisionDocuments.dialog"),
												title: i18n.t("part_revision_documents"),
												data: {
													shopOrderId: row.shopOrderId,
													catalogPartRevisionId: row.catalogPartRevisionId,
												},
											},
											content: <DocumentsOfSingleObjectView objectRef={objectRef} />,
										}));
									}}
								/>
							),
							<OpenShopOrderButton key={"openShopOrder"} shopOrderId={row.shopOrderId} />,
							<OpenCustomerOrderButton key={"openCustomerOrder"} customerOrderId={row.customerOrderId} />,
							<OpenSitePartButton key={"openPart"} partId={row.partId} />,
							editable && (
								<ShopOrderBatchUpdateAttributesMenuButton
									key={"updateAttributes"}
									shopOrderBatchId={shopOrderBatchId}
									row={row}
									allSelectedRows={allSelectedRows}
									onPartAttributesUpdated={refreshData}
								/>
							),
						]}
						agentContext={{
							key: defineAgentUiContextKey("erp.production.shopOrderBatchShopOrders.grid"),
							data: {
								shopOrderBatchId,
								productionLineId: productionLineId ?? null,
							},
							rowFields: ["shopOrderId", "partId", "source", "sourceId", "shopOrderBatchId"],
							selectedRowPathSegment: (row) => `${i18n.t("shop_order")} ${row.shopOrderId}`,
						}}
						{...dataGridProps}
					/>
				);
			}}
		/>
	);
};
